import { Component, OnInit, Inject, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';
import { UserActionService, UserAction } from 'src/app/services/user-action.service';
import { Subject, takeUntil, Observable, combineLatest } from 'rxjs';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { EventConfirmationDialogComponent } from '../calendar/event-confirmation-dialog/event-confirmation-dialog.component';
import { tasksServices } from 'src/app/services/tasks.service';
import { ActivityLeadService } from 'src/app/services/lead-activity.servise';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-user-actions-dialog',
  standalone: true,
  imports: [MaterialModule, CommonModule, FormsModule, MatSnackBarModule],
  templateUrl: './user-actions-dialog.component.html',
  styleUrls: ['./user-actions-dialog.component.scss'],
  
  animations: [
    trigger('listAnimation', [
      transition('* <=> *', [
        query(':enter',
          [
            style({ opacity: 0, transform: 'translateX(100%)' }),
            stagger('30ms',
              animate('200ms ease-out',
                style({ opacity: 1, transform: 'translateX(0)' })
              )
            )
          ],
          { optional: true }
        )
      ])
    ]),
    trigger('newItemAnimation', [
      transition(':enter', [
        style({ 
          opacity: 0, 
          transform: 'translateX(100%)',
          backgroundColor: '#fff3cd'
        }),
        animate('500ms ease-out', style({ 
          opacity: 1, 
          transform: 'translateX(0)',
          backgroundColor: 'transparent'
        }))
      ])
    ])
  ]
})
export class UserActionsDialogComponent implements OnInit, OnDestroy {
  @ViewChild('actionsList') actionsList!: ElementRef;
  
  private destroy$ = new Subject<void>();
  
  currentTab: 'now' | 'today' | 'all' = 'now';
  filteredActions$: Observable<UserAction[]>;
  loading = false;
  private removedActions = new Map<number, UserAction>();

  // Track new actions for animation
  newActionIds = new Set<number>();

  // Simplified - only track snoozing state
  snoozingActionId: number | null = null;
  isCompletingAnyAction = false;

  // Filtered counts for My Actions (computed from showMyAction filter)
  filteredNowCount = 0;
  filteredTodayCount = 0;
  filteredTotalCount = 0;


  constructor(
    public dialogRef: MatDialogRef<UserActionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public actionService: UserActionService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private taskService: tasksServices,
    private activityLeadService: ActivityLeadService,
    private cdr: ChangeDetectorRef
  ) {
    if (data?.initialTab) {
      this.currentTab = data.initialTab;
    }
  }

  async ngOnInit() {
    // Start managing in-memory state
    await this.actionService.loadFullActionsList();
    
    // Set up reactive filtering based on current tab
    this.updateFilteredActions();
    
    // Subscribe to new actions for animations
    this.actionService.newAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(action => {
        this.newActionIds.add(action.id);
        setTimeout(() => {
          this.newActionIds.delete(action.id);
        }, 1000);
      });
  }

  ngOnDestroy() {
    // Stop managing in-memory state when modal closes
    this.actionService.stopManagingInMemoryState();
    
    // Clean up removed actions map
    this.removedActions.clear();
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateFilteredActions() {
    this.filteredActions$ = this.actionService.actions$.pipe(
      map(actions => {
        const now = new Date();

        // Filter active actions
        const activeActions = actions.filter(a => {
          if (!a.status || a.status === 'PENDING') return true;
          if (a.status === 'SNOOZED' && a.snoozedUntil) {
            return new Date(a.snoozedUntil) > now;
          }
          return false;
        });

        // Filter by work queue (My Actions filtering)
        // showMyAction is true if action matches a work queue with show_in_my_actions=true
        // or if the feature is disabled (show all)
        const myActionsFiltered = activeActions.filter(a =>
          a.showMyAction === undefined || a.showMyAction === true
        );

        // Apply tab filter
        let tabFiltered: UserAction[];
        switch (this.currentTab) {
          case 'now':
            tabFiltered = myActionsFiltered.filter(a => a.priority === 'NOW');
            break;
          case 'today':
            tabFiltered = myActionsFiltered.filter(a => a.priority === 'TODAY');
            break;
          case 'all':
            tabFiltered = myActionsFiltered;
            break;
          default:
            tabFiltered = [];
        }

        return { myActionsFiltered, tabFiltered };
      }),
      tap(({ myActionsFiltered }) => {
        // Compute filtered counts for tab badges (deferred to avoid change detection error)
        setTimeout(() => {
          this.filteredNowCount = myActionsFiltered.filter(a => a.priority === 'NOW').length;
          this.filteredTodayCount = myActionsFiltered.filter(a => a.priority === 'TODAY').length;
          this.filteredTotalCount = myActionsFiltered.length;
          this.cdr.detectChanges();
        }, 0);
      }),
      map(({ tabFiltered }) => tabFiltered)
    );
  }

  async setTab(tab: 'now' | 'today' | 'all') {
    if (this.currentTab === tab) return;
    
    this.currentTab = tab;
    this.updateFilteredActions();
  }

  trackByActionId(index: number, action: UserAction): number {
    return action.id;
  }

  getActionIcon(action: UserAction): string {
    const iconMap = {
      'NEW_LEAD': 'person_add',
      'LEAD_RESPONSE': 'message',
      'LEAD_NO_ACTIVITY': 'follow_the_signs',
      'STOP_MESSAGE_RECEIVED': 'block',
      'DOCUMENT_SUBMITTED': 'description',
      'DOCUMENT_REVIEW_NEEDED': 'rate_review',
      'APPLICATION_SUBMITTED': 'assignment',
      'FINANCIAL_AID_SUBMITTED': 'attach_money',
      'DUPLICATE_LEAD_DETECTED': 'people',
      'ACTIVITY_DUE_TODAY': 'today',
      'ACTIVITY_OVERDUE': 'warning',
      'ACTIVITY_UPCOMING': 'schedule'
    };
    return iconMap[action.actionType] || 'notification_important';
  }

  getActionColor(action: UserAction): string {
    if (action.priority === 'NOW') return 'warn';
    if (action.priority === 'TODAY') return 'accent';
    return 'primary';
  }

  isNewAction(action: UserAction): boolean {
    return this.newActionIds.has(action.id);
  }

  async handleAction(action: UserAction) {
    if (this.snoozingActionId === action.id) {
      return;
    }
    
    await this.actionService.markViewed(action.id);
    
    this.dialogRef.close();
    
    switch (action.entityType) {
      case 'LEAD':
        this.router.navigate(['/leads/leads-managment/manage', action.entityId]);
        break;
      case 'MESSAGE':
        const leadId = action.metadata?.leadId;
        if (leadId) {
          this.router.navigate(['/leads/leads-managment/manage', leadId], {
            queryParams: { section: 1 }
          });
        }
        break;
      case 'DOCUMENT':
        const docLeadId = action.metadata?.leadId;
        if (docLeadId) {
          this.router.navigate(['/leads/leads-managment/manage', docLeadId], {
            queryParams: { section: 3 }
          });
        }
        break;
      case 'APPLICATION':
        this.router.navigate(['/admissions/applications', action.entityId]);
        break;
      case 'task':
      case 'TASK':
        this.router.navigate(['/activities/list'], {
          queryParams: { activity_id: action.entityId }
        });
        break;
    }
  }

  async redirectToTask(taskId: any) {
    const response = await firstValueFrom(
      this.taskService.get({id:taskId},'openModal'),
    );

    this.activityLeadService.openActivity({
      ...response, 
      start: new Date(response.startDate), 
      end: new Date(response.endDate), 
      listView: true, 
      hourSegments: 15, 
      activityTypes: [], 
      detailed: true, 
      editMode: true,
      isModal: true
    });
  }

  async quickComplete(event: Event, action: UserAction) {
    event.stopPropagation();
    
    // Check if already completing any action
    if (this.isCompletingAnyAction) {
      return; // Exit early if any completion is in progress
    }
    
    // Set flag to prevent other completions
    this.isCompletingAnyAction = true;
    
    this.snoozingActionId = null;
    
    const completionText = this.getDefaultCompletionText(action);
    
    // Show completion immediately in UI (optimistic update handled by socket)
    const toastRef = this.snackBar.open(
      `Completed: ${action.title}`,
      'UNDO',
      { 
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      }
    );
    
    const dismissSub = toastRef.afterDismissed().subscribe((dismissInfo) => {
      if (dismissInfo.dismissedByAction) {
        this.undoCompleteAction(action);
      }
      dismissSub.unsubscribe();
    });
    
    try {
      await this.actionService.completeAction(action.id, completionText);
    } catch (error) {
      this.snackBar.open('Failed to complete action', 'OK', { duration: 3000 });
      console.error('Error completing action:', error);
    } finally {
      // Reset flag when done (success or failure)
      this.isCompletingAnyAction = false;
    }
  }

  private undoCompleteAction(action: UserAction) {
    this.actionService.reactivateAction(action.id).catch(error => {
      console.error('Error reactivating action:', error);
      this.snackBar.open('Failed to restore action', 'OK', { duration: 3000 });
    });
    
    this.snackBar.open('Action restored', '', { duration: 2000 });
  }

  getDefaultCompletionText(action: UserAction): string {
    const defaults = {
      'NEW_LEAD': 'Reviewed and assigned lead',
      'LEAD_RESPONSE': 'Responded to lead message',
      'LEAD_STEP_CHANGE': 'Updated lead status',
      'LEAD_GONE_SILENT': 'Followed up with lead',
      'STOP_MESSAGE_RECEIVED': 'Removed from messaging',
      'DOCUMENT_SUBMITTED': 'Reviewed document',
      'APPLICATION_SUBMITTED': 'Processed application',
      'FINANCIAL_AID_SUBMITTED': 'Reviewed financial aid',
      'DUPLICATE_LEAD_DETECTED': 'Merged duplicate leads'
    };
    return defaults[action.actionType] || 'Completed action';
  }

  quickSnooze(event: Event, action: UserAction) {
    event.stopPropagation();
  }

  snoozeAction(action: UserAction, hours: number) {
    const snoozeUntil = new Date();
    snoozeUntil.setHours(snoozeUntil.getHours() + hours);
    
    const toastRef = this.snackBar.open(
      `Snoozed until ${this.formatSnoozeTime(snoozeUntil)}`,
      'UNDO',
      { 
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      }
    );
    
    toastRef.onAction().subscribe(() => {
      this.undoSnooze(action);
    });
    
    let newPriority: 'NOW' | 'TODAY' | 'LATER' = action.priority;
    if (hours <= 4) {
      newPriority = 'TODAY';
    } else {
      newPriority = 'LATER';
    }
    
    this.actionService.snoozeAction(action.id, snoozeUntil, newPriority)
      .catch(() => {
        this.snackBar.open('Failed to snooze action', 'OK', { duration: 3000 });
      });
  }

  cancelSnooze() {
    this.snoozingActionId = null;
  }

  getSnoozeTimeHint(hours: number): string {
    const future = new Date();
    future.setHours(future.getHours() + hours);
    
    if (hours === 24) {
      return `Tomorrow ${future.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    return future.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatSnoozeTime(date: Date): string {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === now.toDateString()) {
      return `today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `tomorrow at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    return date.toLocaleString();
  }

  async undoSnooze(action: UserAction) {
    try {
      await this.actionService.reactivateAction(action.id);
    } catch (error) {
      console.error('Error reactivating action:', error);
      this.snackBar.open('Failed to restore action', 'OK', { duration: 3000 });
    }
  }

  close() {
    this.dialogRef.close();
  }

  getTabCount(): number {
    switch (this.currentTab) {
      case 'now': return this.filteredNowCount;
      case 'today': return this.filteredTodayCount;
      case 'all': return this.filteredTotalCount;
      default: return 0;
    }
  }

  getLoadingMessage(): string {
    if (this.loading) return 'Loading actions...';
    return '';
  }

  getDisplayTime(action: UserAction): Date {
    if ((action.actionType === 'ACTIVITY_DUE_TODAY' ||
         action.actionType === 'ACTIVITY_OVERDUE' ||
         action.actionType === 'ACTIVITY_UPCOMING') &&
        action.task?.startDate) {
      return new Date(action.task.startDate);
    }
    
    // For NEW_LEAD, show lead creation time
    if (action.actionType === 'NEW_LEAD' && action.lead?.createdAt) {
      return new Date(action.lead.createdAt);
    }
    
    // For all other actions (messages, documents, LEAD_NO_ACTIVITY, etc.), 
    // show user action creation time
    return new Date(action.createdAt);
  }
}